import React, {Component} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import './layoutContainer.css';
import styled from 'styled-components';
import {withTheme} from 'styled-components';
import classNames from 'classnames';
import Faq from '../Faq';
import parseText from '../../utils/parseText';

const SideBar = styled.div`
  width: ${props => _.get(props, ['theme', 'teaser', 'width'], '90%')};
  min-width: ${props => _.get(props, ['theme', 'teaser', 'minWidth'])};
  grid-area: sidebar;
  margin-top: ${props => _.get(props, ['theme', 'teaser', 'noMarginTop']) ? '0px' : '5px'}; // only for topLeft and topRight
  align-self: ${props => _.includes(['topLeft', 'topRight'], props.position) ? 'start' : 'end'};
  ${props => _.get(props, ['theme', 'teaser', 'justifyOutside']) &&
    `justify-self: ${_.includes(['topLeft', 'bottomLeft'], props.position) ? 'end' : 'start'};`}
  text-align: ${props => _.get(props, ['theme', 'teaser', props.position + 'TextAlignment'])};

  @media (min-width: 769px) {
    margin-left: ${props => _.get(props, ['theme', 'sidebarMarginLeft'], '20px')};
  }

  @media (min-width: 1025px) {
    margin-top: ${props => _.get(props, ['theme', 'teaser', 'sidebarMarginTopWide'])};
    ${props => _.get(props, ['theme', 'teaser', 'stickyWithWideTeaser']) && `
      align-self: stretch;
    `}
  }

  @media (min-width: 481px) and (max-width: 768px) {
    text-align: left;
    width: 100%;
  }

  @media (min-width: 364px) and (max-width: 480px) {
    text-align: left;
    width: 100%;
  }
`;

const StickyTeaser = styled.div`
  @media (min-width: 1025px) {
    ${props => _.get(props, ['theme', 'teaser', 'stickyWithWideTeaser']) && `
      position: sticky;
      top: ${_.get(props, ['theme', 'teaser', 'stickySidebarTopWide'])};
    `}
  }
`;

const Footer = styled.div`
  grid-template-columns: ${props => _.get(props, ['theme', 'footerGridTemplateColumns'])};

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: ${props => _.get(props, ['theme', 'landscapeTabletFooterGridTemplateColumns'])};
  }

  @media (min-width: 481px) and (max-width: 768px) {
    grid-template-columns: ${props => _.get(props, ['theme', 'tabletFooterGridTemplateColumns'])};
  }

  @media (max-width: 480px) {
    grid-template-columns: ${props => _.get(props, ['theme', 'mobileFooterGridTemplateColumns'])};
  }
`;

const Content = styled.div`
  grid-template-areas: ${props => _.get(props, ['theme', 'gridTemplateAreas'])};
  grid-template-rows: ${props => _.get(props, ['theme', 'gridTemplateRows'])};
  grid-template-columns: ${props => _.get(props, ['theme', 'gridTemplateColumns'])};

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-areas: ${props => _.get(props, ['theme', 'landscapeTabletGridTemplateAreas'])};
    grid-template-rows: ${props => _.get(props, ['theme', 'landscapeTabletGridTemplateRows'])};
    grid-template-columns: ${props => _.get(props, ['theme', 'landscapeTabletGridTemplateColumns'])};
  }

  @media (min-width: 481px) and (max-width: 768px) {
    grid-template-areas: ${props => _.get(props, ['theme', 'tabletGridTemplateAreas'])};
    grid-template-rows: ${props => _.get(props, ['theme', 'tabletGridTemplateRows'])};
    grid-template-columns: ${props => _.get(props, ['theme', 'tabletGridTemplateColumns'])};
    &.showCollectingWidget {
      grid-template-areas: ${props => _.get(props, ['theme', 'tabletGridTemplateAreasCollecting'])};
    }

    &.djsbMobileMarginTop > .djsbMargin {
      height: ${props => _.get(props, ['theme', 'djsb', 'tabletMarginTop'])};
    }
    &.djsbMobileMarginTop > .smallDjsbMargin {
      height: ${props => _.get(props, ['theme', 'djsb', 'smallTabletMarginTop'])};
    }
  }

  @media (max-width: 480px) {
    grid-template-areas: ${props => _.get(props, ['theme', 'mobileGridTemplateAreas'])};
    grid-template-rows: ${props => (['2', '3'].includes(props.step) && _.get(props, ['theme', 'mobileGridTemplateRowsStep23'])) ||
      _.get(props, ['theme', 'mobileGridTemplateRows'])};
    grid-template-columns: ${props => _.get(props, ['theme', 'mobileGridTemplateColumns'])};
    &.showCollectingWidget {
      grid-template-areas: ${props => _.get(props, ['theme', 'mobileGridTemplateAreasCollecting'])};
    }

    &.djsbMobileMarginTop > .djsbMargin {
      height: ${props => _.get(props, ['theme', 'djsb', 'mobileMarginTop'])};
    }
    &.djsbMobileMarginTop > .smallDjsbMargin {
      height: ${props => _.get(props, ['theme', 'djsb', 'smallMobileMarginTop'])};
    }
  }
`;

class LayoutContainer extends Component {
  static propTypes = {
    header: PropTypes.element,
    content: PropTypes.element.isRequired,
    sidebar: PropTypes.element,
    footer: PropTypes.element,
    position: PropTypes.string,
    showCollectingWidget: PropTypes.bool,
    formContext: PropTypes.object,
    step: PropTypes.string
  };

  static defaultProps = {
    position: 'bottomLeft'
  };

  render() {
    const faq = _.isNil(this.props.faq) || _.size(this.props.faq) === 0
      ? null
      : <>
        <div className='blankLeft' />
        <div className='faq'>
          <Faq questions={this.props.faq} closed={this.props.formContext?.faqClosed} />
        </div>
        <div className='blankRight' />
      </>;

    const footerHtml = parseText(this.props.formContext || {}, this.props.footer);
    const footer = <Footer className='footer'>
      {faq}
      {!_.isNil(this.props.footer) && <div className='row2' dangerouslySetInnerHTML={{__html: footerHtml}} />}
    </Footer>;

    const smallHeader = _.has(this, ['props', 'theme', 'smallHeader'])
      ? _.get(this, ['props', 'theme', 'smallHeader'])
      : false;
    const containerClasses = classNames({
      containerLeft: _.includes(['topLeft', 'bottomLeft'], this.props.position),
      container: _.includes(['topRight', 'bottomRight'], this.props.position),
      smallHeader: smallHeader,
      showCollectingWidget: this.props.showCollectingWidget,
      djsbMobileMarginTop: _.get(this.props.theme, ['djsb', 'showDJSB']) || _.get(this.props.theme, ['djsb', 'showDJSBMargin']), // && _.get(this.props.formContext, ['externalReferenceName'])
      step1: this.props.step === '1'
    });

    let content = <Content className={containerClasses} step={this.props.step}>
      <div className='header'>{this.props.header}</div>
      <div className={_.get(this.props.formContext, ['externalReferenceName']) ? 'djsbMargin' : 'smallDjsbMargin'} />
      <div className={classNames('contentBottomLeft', {
        stickyContent: _.get(this.props.theme, ['boxComponent', 'stickyWithWideTeaser'])
      })}>{this.props.content}</div>
      <SideBar showCollectingWidget={this.props.showCollectingWidget} position={this.props.position}><StickyTeaser>{this.props.sidebar}</StickyTeaser></SideBar>
      {footer}
    </Content>;

    if (this.props.position === 'topLeft') {
      content = <Content className={containerClasses} step={this.props.step}>
        <div className='header'>{this.props.header}</div>
        <div className={_.get(this.props.formContext, ['externalReferenceName']) ? 'djsbMargin' : 'smallDjsbMargin'} />
        <div className={classNames('contentTopLeft', {
          stickyContent: _.get(this.props.theme, ['boxComponent', 'stickyWithWideTeaser'])
        })}>{this.props.content}</div>
        <SideBar showCollectingWidget={this.props.showCollectingWidget} position={this.props.position}><StickyTeaser>{this.props.sidebar}</StickyTeaser></SideBar>
        {footer}
      </Content>;
    } else if (this.props.position === 'topRight') {
      content = <Content className={containerClasses} step={this.props.step}>
        <div className='header'>{this.props.header}</div>
        <div className={_.get(this.props.formContext, ['externalReferenceName']) ? 'djsbMargin' : 'smallDjsbMargin'} />
        <div className={classNames('contentTopRight', {
          stickyContent: _.get(this.props.theme, ['boxComponent', 'stickyWithWideTeaser'])
        })}>{this.props.content}</div>
        <SideBar showCollectingWidget={this.props.showCollectingWidget} position={this.props.position}><StickyTeaser>{this.props.sidebar}</StickyTeaser></SideBar>
        {footer}
      </Content>;
    } else if (this.props.position === 'bottomRight') {
      content = <Content className={containerClasses} step={this.props.step}>
        <div className='header'>{this.props.header}</div>
        <div className={_.get(this.props.formContext, ['externalReferenceName']) ? 'djsbMargin' : 'smallDjsbMargin'} />
        <div className={classNames('contentBottomRight', {
          stickyContent: _.get(this.props.theme, ['boxComponent', 'stickyWithWideTeaser'])
        })}>{this.props.content}</div>
        <SideBar showCollectingWidget={this.props.showCollectingWidget} position={this.props.position}><StickyTeaser>{this.props.sidebar}</StickyTeaser></SideBar>
        {footer}
      </Content>;
    }

    return (content);
  }
}

export default withTheme(LayoutContainer);
